import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Allowed parent origin for SSO postMessage. Falls back to same-origin if unset.
const PARENT_ORIGIN =
  process.env.REACT_APP_SSO_PARENT_ORIGIN ||
  (typeof window !== "undefined" ? window.location.origin : "");

// -----------------------------------------------------------------------------
// axios client — cookie-based auth.
// The backend sets `access_token` as HttpOnly, Secure, SameSite=None on
// /auth/login, /auth/register, /auth/google, /auth/sso/parent. We keep the
// token OUT of localStorage entirely so it can't be exfiltrated by XSS. All
// API calls run with `withCredentials: true` so the cookie is auto-sent
// cross-origin.
// -----------------------------------------------------------------------------
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auto-notify the app when the session expires. We surface a global event
// instead of touching state here so any route can react (redirect, toast).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      try { window.dispatchEvent(new CustomEvent("awg:auth-expired")); } catch (e) { /* noop */ }
    }
    return Promise.reject(error);
  },
);

const AuthContext = createContext(null);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inIframe = () => {
  try { return window.self !== window.top; } catch (e) { return true; }
};

const postToParent = (payload) => {
  if (!inIframe() || !PARENT_ORIGIN) return;
  try { window.parent.postMessage(payload, PARENT_ORIGIN); }
  catch (err) { console.error("[AWG] postMessage to parent failed:", err); }
};

const replyToSender = (ev, payload) => {
  if (!ev?.source || !PARENT_ORIGIN) return;
  if (ev.origin !== PARENT_ORIGIN) return;
  try { ev.source.postMessage(payload, PARENT_ORIGIN); }
  catch (err) { console.error("[AWG] postMessage reply failed:", err); }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const ssoExchanging = useRef(false);

  useEffect(() => {
    let alive = true;

    // Bootstrap: attempt /auth/me. If the httpOnly cookie is present the
    // request succeeds; otherwise 401 → user is unauthenticated. We never
    // look at localStorage here.
    const bootstrap = async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (alive) setUser(data);
        return data;
      } catch (err) {
        if (alive) setUser(false);
        return null;
      }
    };

    // SSO listener (parent-site postMessage handoff)
    const onMessage = async (ev) => {
      if (PARENT_ORIGIN && ev.origin !== PARENT_ORIGIN) return;
      const msg = ev?.data;
      if (!msg || typeof msg !== "object") return;
      if (msg.type !== "AWG_SSO_TOKEN" || !msg.token) return;
      try {
        ssoExchanging.current = true;
        const { data } = await api.post("/auth/sso/parent", { token: msg.token });
        if (alive) {
          setUser(data);
          setReady(true);
        }
        replyToSender(ev, {
          type: "AWG_SSO_OK",
          user: { email: data?.email, role: data?.role },
        });
      } catch (err) {
        console.error("[AWG] SSO exchange failed:", err);
        replyToSender(ev, {
          type: "AWG_SSO_ERROR",
          message: err?.response?.data?.detail || err?.message || "SSO failed",
        });
      } finally {
        ssoExchanging.current = false;
      }
    };
    window.addEventListener("message", onMessage);

    // React to global 401s (interceptor above dispatches this).
    const onExpired = () => {
      if (!alive) return;
      setUser(false);
    };
    window.addEventListener("awg:auth-expired", onExpired);

    postToParent({ type: "AWG_SSO_READY" });

    (async () => {
      const me = await bootstrap();
      if (!alive) return;
      if (me) { setReady(true); return; }
      // If we're in an iframe with no session and the parent has SSO plumbed
      // in, it will post the token now. Delay the "no user" state briefly so
      // the iframe doesn't flash the login screen before SSO lands.
      if (inIframe()) {
        setTimeout(() => {
          if (alive && !ssoExchanging.current && user === false) {
            setReady(true);
          }
        }, 1500);
      } else {
        setReady(true);
      }
    })();

    return () => {
      alive = false;
      window.removeEventListener("message", onMessage);
      window.removeEventListener("awg:auth-expired", onExpired);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Server has set the httpOnly cookie — we only keep the user profile.
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data?.token) setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.error("[AWG] Logout request failed:", err?.message || err); }
    setUser(false);
    postToParent({ type: "AWG_SSO_LOGOUT" });
  };

  const refreshMe = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const value = useMemo(
    () => ({ user, ready, login, register, logout, refreshMe, setUser }),
    [user, ready],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
